import { triggerRefreshFlag, RefreshFlag } from "@/refresh_flag.tsx"
import { UserState } from "@/auth/user.tsx"
import { useState } from "preact/hooks"
import { api_fetch, api_fetch_throws, TransactionJson, TransactionKind } from "@/api"
import { GlobalFilterState } from "@/global_filter"
import { OrgConfigState } from "@/org_config/state.tsx"
import { useMemo, useRef } from "preact/compat"
import { backupParseColumn, type TableColumn } from "@/deduction_table/columns.tsx"
import { ACCOUNTING_COLUMNS, ACCOUNTING_COLUMNS_WITHOUT_BACKUP_PARSE, createAccountingOrgColumns } from "@/transactions/columns.tsx"
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  Table,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { dateFilter, defaultFilter, fuzzyFilter } from "@/utils/filters.ts"
import { toast } from "@/components/ui/use-toast.ts"
import { isGuayakiUser, map, toISODateString, useAsyncEffect, usePageTitle } from "@/utils"
import { DeductionNavbar, GlobalFilters } from "@/app/navbar.tsx"
import { ExpenseAccountsBreakdown } from "@/transactions/ExpenseAccountsBreakdown.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Loader2, WandSparklesIcon } from "lucide-react"
import { TransactionTable, KindAndCodedFilters } from "./TransactionTable.tsx"
import { AccountingState, handleTransactionSearch, INITIAL_ACCOUNTING_STATE } from "@/transactions/state.tsx"
import { Link } from "wouter-preact"
import { FILTERED_TABLE_ANCHOR_ID } from "@/utils/filter_feedback"

interface TransactionsProps {
  kinds: TransactionKind[]
  titleSection?: React.ReactNode
}

export function Transactions() {
  usePageTitle("Transactions")
  let kinds = ["deduction", "invoice", "prepayment", "repayment"] as TransactionKind[]
  let page_title = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Transactions</h1>
        <span className="text-gray-500 text-sm block">Review deductions, invoices, prepayments, and repayments.</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link to="/transactions/deductions">
          <Button variant="outline">Deductions</Button>
        </Link>
      </div>
    </div>
  )
  return <TransactionsContainer kinds={kinds} titleSection={page_title} />
}

export function TransactionsContainer({ titleSection, kinds }: TransactionsProps) {
  const refreshFlag = RefreshFlag.use(sf => sf!)
  const user = UserState.use()
  const accountingState = AccountingState.use(as => as!)

  const [loading, setLoading] = useState(true)
  const [autoCodeLoading, setAutoCodeLoading] = useState(false)
  const [data, setData] = useState<TransactionJson[]>([])
  const fetch_id = useRef(0)
  const orgConfig = OrgConfigState.use()

  const { startDate: start, endDate: end, distributor, dateKind } = GlobalFilterState.use()

  const columns = useMemo(() => {
    if (!user) return ACCOUNTING_COLUMNS
    const orgColumns = createAccountingOrgColumns(user.org_id!, orgConfig.orgFieldConfigs)
    return [
      ...ACCOUNTING_COLUMNS_WITHOUT_BACKUP_PARSE,
      ...orgColumns,
      backupParseColumn as TableColumn<TransactionJson>,
    ]
  }, [user?.org_id, orgConfig.orgFieldConfigs])

  const table = useMemo<Table<TransactionJson>>(() => {
    return useReactTable({
      data: data,
      columns: columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      globalFilterFn: fuzzyFilter,
      filterFns: {
        dateFilter: dateFilter,
        default: defaultFilter,
      },
      // see comments on this config in DeductionTable.tsx
      autoResetPageIndex: false,
      // see comments on this config in DeductionTable.tsx
      getColumnCanGlobalFilter: c => c.columnDef.enableGlobalFilter ?? true,
      onColumnFiltersChange: function (updater: any) {
        const filters = updater(accountingState.columnFilters!) as ColumnFiltersState
        AccountingState.set(state => ({ ...state, columnFilters: filters }))
      },
      onColumnVisibilityChange: function (updater: any) {
        const visibility = updater(accountingState.columnVisibility!) as VisibilityState
        AccountingState.set(state => ({ ...state, columnVisibility: visibility }))
      },
      onPaginationChange: function (updater: any) {
        AccountingState.set(state => {
          let pagination = updater(state.pagination) as PaginationState
          return { ...state, pagination }
        })
      },
      onGlobalFilterChange: handleTransactionSearch,
      onRowSelectionChange: function (updater: any) {
        const selection = updater(accountingState.rowSelection) as RowSelectionState
        AccountingState.set(state => ({ ...state, rowSelection: selection }))
      },
      onSortingChange: function (updater: any) {
        let new_sort = updater(accountingState.sorting!) as SortingState
        AccountingState.set(s => ({
          ...s,
          sorting: new_sort,
        }))
      },
      state: {
        columnVisibility: accountingState.columnVisibility,
        rowSelection: accountingState.rowSelection,
        sorting: accountingState.sorting,
        globalFilter: accountingState.search,
        columnFilters: accountingState.columnFilters,
        pagination: accountingState.pagination,
      },
      meta: {
        resetRowSelection: () => {
          AccountingState.set(state => ({ ...state, rowSelection: {} }))
        },
        clearAllFilters: () => {
          AccountingState.set(current => ({
            ...INITIAL_ACCOUNTING_STATE,
            columnVisibility: current.columnVisibility,
            sorting: current.sorting,
          }))
        },
      },
      initialState: {
        pagination: {
          pageSize: 50,
        },
      },
    })
  }, [data, accountingState, columns])

  async function handleAutoCode() {
    setAutoCodeLoading(true)
    const res = await api_fetch<{ coded_count: number; skipped_zero_amount?: number }>("/auto-code-deductions", {
      method: "POST",
      body: [],
    })
    setAutoCodeLoading(false)
    if (!res.ok) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to auto-code deductions",
      })
      return
    }
    const coded_count = res.value.data?.coded_count ?? 0
    const skipped_zero = res.value.data?.skipped_zero_amount ?? 0
    const skipped_note =
      skipped_zero > 0 ? ` (${skipped_zero} skipped with $0 amount)` : ""
    toast({
      title: "Auto-Code Complete",
      description: `Successfully coded ${coded_count} deduction${coded_count === 1 ? "" : "s"}${skipped_note}`,
    })
    triggerRefreshFlag()
  }

  async function requestData(): Promise<TransactionJson[]> {
    const request_id = ++fetch_id.current
    const rows = await api_fetch_throws<TransactionJson[]>("/v2/accounting/list", {
      method: "POST",
      body: {
        start_date: map(start, toISODateString),
        end_date: map(end, toISODateString),
        distributor,
        kinds,
        date_kind: dateKind,
      },
    })
    if (request_id !== fetch_id.current) {
      return rows
    }
    setLoading(false)
    setData(rows)
    return rows
  }

  // Guayaki-only export hook: optionally run the RTM auto-split, then refetch and return only the
  // rows currently visible in the filtered table (transactionIds).
  async function getExportRows({
    autoSplit,
    transactionIds,
  }: {
    autoSplit: boolean
    transactionIds: string[]
  }): Promise<TransactionJson[]> {
    const idSet = new Set(transactionIds)
    function filterToExportScope(rows: TransactionJson[]): TransactionJson[] {
      return rows.filter((row: TransactionJson) => idSet.has(row.id))
    }

    if (autoSplit) {
      const res = await api_fetch<{ split_count: number }>("/auto-split-rtm", {
        method: "POST",
        body: {
          start_date: map(start, toISODateString),
          end_date: map(end, toISODateString),
          distributor,
        },
      })
      if (!res.ok) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to auto-split RTM before export",
        })
        // Fall through and export the current filtered data rather than blocking the export entirely.
        return filterToExportScope(data)
      }
      const split_count = res.value.data?.split_count ?? 0
      if (split_count === 0) {
        return filterToExportScope(data)
      }
      triggerRefreshFlag()
    }
    return filterToExportScope(await requestData())
  }

  useAsyncEffect(async () => {
    setLoading(true)
    await requestData()
    AccountingState.set(s => {
      let pagination = s.pagination
      pagination.pageIndex = 0
      return { ...s, pagination }
    })
  }, [start, end, distributor, dateKind])

  useAsyncEffect(async () => {
    await requestData()
  }, [refreshFlag])

  return (
    <div className="w-full">
      <div className="px-4 space-y-4">
        <DeductionNavbar>
          <GlobalFilters />
        </DeductionNavbar>
        {titleSection}
      </div>
      <div className="flex justify-between gap-4 px-4 items-end">
        <div className="grow min-w-xl flex-col justify-between">
          <ExpenseAccountsBreakdown table={table} />
        </div>
        <div className="flex flex-col items-end w-full ml-auto space-y-4">
          <KindAndCodedFilters data={data} kinds={kinds} />
          <div className="flex gap-4">
            <Button variant="default" onClick={handleAutoCode} disabled={autoCodeLoading}>
              {autoCodeLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <WandSparklesIcon className="w-4 h-4 mr-2" />
              )}
              Auto Code
            </Button>
          </div>
        </div>
      </div>
      <div id={FILTERED_TABLE_ANCHOR_ID} className="mx-auto mb-16 scroll-mt-4">
        <TransactionTable
          loading={loading}
          table={table}
          orgFieldConfigs={orgConfig.orgFieldConfigs}
          getExportRows={isGuayakiUser(user?.org_id) ? getExportRows : undefined}
        />
      </div>
    </div>
  )
}
